/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { createReferral, type IntakePerson, type ReferralInsurance } from "../api/referrals";
import { listOrganizations, organizationDetail } from "../api/registry";
import { newIdempotencyKey } from "../api/client";
import { OrganizationProviderPicker, type PickerOption } from "../components/OrganizationProviderPicker";
import { InsuranceFields } from "../components/InsuranceFields";
import { IntakePersonFields, cleanPerson } from "../components/IntakePersonFields";
import { BusyLabel } from "../components/ui/BusyLabel";
import { Button } from "../components/ui/Button";
import { ErrorMessage } from "../components/ui/ErrorMessage";
import { Textarea } from "../components/ui/Textarea";
import { useChrome } from "../contexts/ChromeContext";
import { useToast } from "../contexts/ToastContext";
import { backHref } from "../lib/navigation";
import { organizationOptions, providerOptions } from "../lib/registry";
import { blankToNull } from "../lib/utils";
import { ValidationError } from "../lib/validation";

const EMPTY_PERSON: IntakePerson = {
  firstName: null,
  lastName: null,
  email: null,
  phoneNumber: null,
  dob: null,
  stateCode: null,
};

const NO_INSURANCE: ReferralInsurance = { payerName: null, memberId: null, groupNumber: null };

/**
 * A referral that arrived by phone or email: staff record the family and who sent them, and
 * nexus takes it from there exactly as it takes a faxed or web-form referral.
 */
export function ReferralNew() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  useChrome({ back: { to: "/referrals", label: "Referrals" } }, []);

  const [organizationId, setOrganizationId] = useState("");
  const [providerId, setProviderId] = useState("");
  const [providers, setProviders] = useState<PickerOption[]>([]);
  const [child, setChild] = useState<IntakePerson>(EMPTY_PERSON);
  const [caregiver, setCaregiver] = useState<IntakePerson>(EMPTY_PERSON);
  const [concerns, setConcerns] = useState("");
  const [insurance, setInsurance] = useState<ReferralInsurance>(NO_INSURANCE);
  const [note, setNote] = useState("");
  const [error, setError] = useState<ValidationError | null>(null);
  const [busy, setBusy] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);

  useEffect(() => {
    if (organizationId === "") {
      setProviders([]);
      return;
    }
    let cancelled = false;
    void organizationDetail(organizationId).then((result) => {
      if (!cancelled) {
        setProviders(result.ok ? providerOptions(result.value.providers) : []);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  const fieldError = (field: string) => error?.getFieldError(field) ?? "";
  const clearField = (field: string) =>
    setError((previous) => previous?.withoutField(field) ?? null);

  function personChanged(setPerson: (next: IntakePerson) => void) {
    return (next: IntakePerson, field: string) => {
      setPerson(next);
      clearField(field);
      setIdempotencyKey(newIdempotencyKey());
    };
  }

  const named = (person: IntakePerson) => {
    const clean = cleanPerson(person);
    return clean.firstName !== null && clean.lastName !== null;
  };
  const ready = organizationId !== "" && named(child) && named(caregiver);

  async function submit() {
    setBusy(true);
    setError(null);
    const result = await createReferral(
      {
        organizationId: Number(organizationId),
        providerId: providerId === "" ? null : Number(providerId),
        child: cleanPerson(child),
        caregiver: cleanPerson(caregiver),
        concerns: blankToNull(concerns),
        diagnosisCodes: [],
        insurance,
        note: blankToNull(note),
      },
      idempotencyKey,
    );
    setBusy(false);
    if (!result.ok) {
      setIdempotencyKey(newIdempotencyKey());
      setError(ValidationError.fromApiError(result.error));
      return;
    }
    showToast("success", `Referral ${result.value.id} created.`);
    void navigate(`/referrals/${result.value.id}`, { state: location.state });
  }

  return (
    <div className="space-y-8">
      <header className="enter">
        <h1 className="font-display text-2xl leading-tight text-text-primary sm:text-[1.75rem]">
          New referral
        </h1>
        <p className="mt-2 text-[15px] leading-relaxed text-text-secondary">
          For a referral that came by phone or email. Record the family and who sent them; the
          family hears from us the same way they would after a fax.
        </p>
      </header>

      <div className="space-y-6 rounded-3xl border border-border bg-bg-elevated p-5 shadow-card sm:p-6">
        <ErrorMessage message={error?.errorMessage ?? null} className="mb-0" />

        <OrganizationProviderPicker
          organization={{
            value: organizationId,
            onChange: (value) => {
              setOrganizationId(value);
              setProviderId("");
              setIdempotencyKey(newIdempotencyKey());
            },
            fetchOptions: async (search) => {
              const result = await listOrganizations(search.trim(), 0);
              return result.ok ? organizationOptions(result.value.items) : [];
            },
            placeholder: "Search organizations…",
          }}
          provider={{
            value: providerId,
            onChange: (value) => {
              setProviderId(value);
              setIdempotencyKey(newIdempotencyKey());
            },
            options: providers,
            fallback: "",
            placeholder:
              organizationId === "" ? "Choose the organization first" : "Choose a provider",
          }}
          providerHint="Optional. A referral without a named provider is credited to the organization."
        />

        <IntakePersonFields
          role="child"
          person={child}
          onChange={personChanged(setChild)}
          fieldError={fieldError}
        />
        <IntakePersonFields
          role="caregiver"
          person={caregiver}
          onChange={personChanged(setCaregiver)}
          fieldError={fieldError}
          stateOptional
        />
        <p className="text-sm text-text-secondary">
          A phone number or an email is how we reach the family. Leave the caregiver's state blank
          when it is the child's.
        </p>

        <section className="space-y-3">
          <h3 className="font-display text-base text-text-primary">What the office told us</h3>
          <Textarea
            label="Concerns"
            value={concerns}
            onChange={(event) => {
              setConcerns(event.target.value);
              setIdempotencyKey(newIdempotencyKey());
            }}
            placeholder="The reason for the referral, in the office's words"
          />
          <InsuranceFields
            value={insurance}
            onChange={(next) => {
              setInsurance(next);
              clearField("insurance.payerName");
              setIdempotencyKey(newIdempotencyKey());
            }}
            errors={(field) => error?.getFieldError(field)}
          />
          <Textarea
            label="Note (optional)"
            value={note}
            onChange={(event) => {
              setNote(event.target.value);
              setIdempotencyKey(newIdempotencyKey());
            }}
            placeholder="Anything else the office or the family said"
          />
        </section>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" disabled={busy} onClick={() => void navigate(backHref("/referrals", location.state))}>
            Cancel
          </Button>
          <Button size="sm" disabled={busy || !ready} onClick={() => void submit()}>
            <BusyLabel busy={busy} busyText="Creating…">
              Create referral
            </BusyLabel>
          </Button>
        </div>
      </div>
    </div>
  );
}
